import { Stack, SwipeableDrawer } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GridSortModel } from '@mui/x-data-grid';
import { useCallback, useEffect } from 'react';
import * as React from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { useLocalStorage } from 'usehooks-ts';
import { AppLoader } from '@common-components';
import { ApiKey } from 'api';
import { LocalStorageKey } from 'config';
import { PrivateChatBulkMessageStatus, RoutePath } from 'enums';
import {
  useGetPrivateChatBulkMessages,
  useIsMobile,
  useSearch,
  useTableHeight,
} from 'hooks';
import { useGetChatLeanActivity } from 'hooks/api/project/useGetChatLeanActivity';
import { iOS } from 'pages/app-pages/chatbot-builder-page/toolbox/props-config';
import ChatRecipientsDrawer from 'pages/app-pages/private-bulk-message-page/chat-recipients-drawer/ChatRecipientsDrawer';
import {
  FormMode,
  PrivateBulkMessageFormLocalState,
} from 'pages/app-pages/private-bulk-message-page/message-modal/types';
import MessagesTableWrapper from 'pages/app-pages/private-bulk-message-page/MessagesTableWrapper';
import { sortByMenuItems } from 'pages/app-pages/private-bulk-message-page/SortByMessages';
import { useBulkDrawer } from 'pages/app-pages/private-bulk-message-page/store/useBulkDrawer';
import BulkMessageTopBar from 'pages/app-pages/private-bulk-message-page/top-bar/BulkMessageTopBar';
import { BulkMessageColumns } from 'pages/app-pages/private-bulk-message-page/types';
import { Project } from 'types';

const drawerWidth = 360;

const Main = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile',
})<{
  open?: boolean;
  isMobile?: boolean;
}>(({ theme, open, isMobile }) => ({
  backgroundColor: theme.palette.grey[50],
  flexGrow: 1,
  overflowX: isMobile ? 'visible' : 'clip',
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: isMobile ? 0 : `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  }),
}));

const PrivateBulkPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();

  const { openDrawer, toggleDrawer, drawerOpen, closeDrawer } = useBulkDrawer((state) => ({
    closeDrawer: state.closeDrawer,
    openDrawer: state.openDrawer,
    toggleDrawer: state.toggleDrawer,
    drawerOpen: state.drawerOpen,
  }));

  const gridTableHeight = useTableHeight({ subtract: -24 });

  const { data: project, isLoading } = useSWR<Project>(`${ApiKey.Project}/${id}`, {
    onError: (error) => {
      if (error?.response?.status === 404) {
        navigate(`/${RoutePath.NotFound}`, { replace: true });
      }
    },
  });

  const { chatGroupMessages, isLoading: isMessagesLoading } = useGetPrivateChatBulkMessages({
    projectId: id || '',
  });

  const {count:projectChatsCount, isLoading: chatDataIsLoading  } = useGetChatLeanActivity(id);

  const [sortModel, setSortModel] = useLocalStorage<GridSortModel>(
    LocalStorageKey.MessagesViewSort,
    [
      {
        field: BulkMessageColumns.UpdatedAt,
        sort: 'desc',
      },
    ],
  );

  const navigateToOnboarding = useCallback(() => {
    if (isLoading || isMessagesLoading || chatDataIsLoading) {
      return;
    }

    if (projectChatsCount === 0) {
      navigate(RoutePath.Onboarding, { replace: true });
    }
  }, [chatDataIsLoading, isLoading, isMessagesLoading, navigate, projectChatsCount]);

  useEffect(() => {
    navigateToOnboarding();
  }, [navigateToOnboarding]);

  const {
    search,
    handleSearchChange,
    filteredItems: filteredMessages,
    //  noSearchResults,
    //  resetSearch,
  } = useSearch({
    data: chatGroupMessages,
    searchKey: ['message'],
  });

  const messagesConfig = sortByMenuItems.find((item) => item.field === sortModel[0].field);
  const currentSortDirection = sortModel[0].sort;

  const filteredAndSortedMessages =
    filteredMessages.sort(
      currentSortDirection === 'asc'
        ? messagesConfig?.sorter
        : (a, b) => messagesConfig?.sorter(b, a) || 0,
    ) || [];

  const onSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  };

  if (isLoading) {
    return <AppLoader />;
  }

  return (
    <>
      <Stack width={1} height={1}>
        <BulkMessageTopBar name={project?.name || ''} />
        <Stack
          flex={1}
          direction={isMobile ? 'column' : 'row'}
          sx={{
            overflowX: 'clip',
          }}
        >
          <Main open={drawerOpen} isMobile={isMobile}>
            <MessagesTableWrapper
              onCreateMessage={() => {
                if (!project?.token) {
                  navigate(RoutePath.Publish, { replace: true });
                } else {
                  navigate(RoutePath.Composer, {
                    replace: true,
                    state: {
                      projectId: id,
                      mode: FormMode.Create,
                      messageStatus: PrivateChatBulkMessageStatus.Published,
                      initialValues: {
                        project: id,
                      },
                    } as PrivateBulkMessageFormLocalState,
                  });
                }
              }}
              handleSearchChange={handleSearchChange}
              search={search}
              toggleDrawer={toggleDrawer}
              drawerIsOpen={drawerOpen}
              isLoading={isLoading || isMessagesLoading}
              messages={filteredAndSortedMessages}
              onSortModelChange={onSortModelChange}
              sortModel={sortModel}
            />
          </Main>
          <SwipeableDrawer
            anchor={isMobile ? 'bottom' : 'right'}
            variant={isMobile ? 'temporary' : 'persistent'}
            className='notranslate'
            disableBackdropTransition={!iOS}
            disableDiscovery
            disableSwipeToOpen
            open={drawerOpen}
            onOpen={openDrawer}
            sx={{
              //  order: isMobile ? 1 : 0,
              width: isMobile ? '100%' : drawerWidth,
              height: isMobile ? '80%' : gridTableHeight,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                borderTopLeftRadius: isMobile ? 8 : 0,
                borderTopRightRadius: isMobile ? 8 : 0,
                width: isMobile ? '100%' : drawerWidth,
                boxSizing: 'border-box',
                position: isMobile ? 'inherit' : 'static',
              },
            }}
            // @ts-ignore
            onClose={(_, reason) => {
              if (reason === 'backdropClick') {
                closeDrawer();
              } else {
                closeDrawer();
              }
            }}
          >
            <ChatRecipientsDrawer />
          </SwipeableDrawer>
        </Stack>
      </Stack>
      <Outlet />
    </>
  );
};

export default PrivateBulkPage;
